import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * Settings → Easeedesk → Adam Intop → Python files.
 * Perechile vechi: `settings/easeedesk/adam_intop_python_files`,
 * `getAdamIntopPythonFiles`, `edit_python_file`, `create_python_file_save`,
 * `edit_python_file_save`, `delete_python_file`, `edit_file_hierarchy`,
 * `save_python_files_order`.
 *
 * `apiGet`/`apiPost` pun singure `/api/v2` in fata caii - de-aia caile de aici
 * incep de la `/settings`.
 */

const BASE = '/settings/easeedesk/adam_intop/python_files'

export interface PythonModule {
  ID: number
  module_name: string
}

export interface PythonFileBrief {
  ID: number
  filename: string
  module_id: number
}

export interface PythonFilesShell {
  title: string
  moduleUrl: string
  modules: PythonModule[]
  files: PythonFileBrief[]
}

export interface PythonFileRow {
  ID: number
  filename: string
  filepath: string
  module_id: number
  module_name: string
  parent_of_file: string
  child_of_file: string
  file_order: string
  type: string
  status: string
  DT_RowId: string
}

export interface PythonFileTablePage {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: PythonFileRow[]
}

export interface PythonFileDetails {
  ID: number
  filename: string
  module_id: number
  filepath: string
  parent_of_file: string
  child_of_file: string
  file_order: string
  type: string
}

export interface PythonFilePage {
  file: PythonFileDetails
  modules: PythonModule[]
  files: PythonFileBrief[]
  /** null = serverul de fisiere n-a raspuns; editorul ramane stins. */
  content: string | null
}

export interface PythonHierarchyFile extends PythonFileBrief {
  child_of_file: string
}

export interface PythonHierarchyPage {
  module_id: number
  modules: PythonModule[]
  files: PythonHierarchyFile[]
}

export interface PythonFileForm {
  filename: string
  filepath: string
  parent_of_file: string
  child_of_file: string
  file_order: string
  type: string
  module_id: string
}

export interface PythonTableRequest {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName: string
  orderDir: 'asc' | 'desc'
  search: string
}

/** Aceiasi parametri pe care ii trimitea DataTables pe `getAdamIntopPythonFiles`. */
export function pythonTableParams(r: PythonTableRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  return p
}

export function pythonFilesShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'adam_intop', 'python_files'],
    queryFn: () => apiGet<PythonFilesShell>(BASE),
  })
}

export function pythonFileTableQuery(r: PythonTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'adam_intop', 'python_files', 'table', r],
    queryFn: () => apiGet<PythonFileTablePage>(`${BASE}/table?${pythonTableParams(r).toString()}`),
  })
}

export function pythonFileQuery(file: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'adam_intop', 'python_files', 'one', file],
    queryFn: () => apiGet<PythonFilePage>(`${BASE}/${file}`),
    enabled: file > 0,
  })
}

export function pythonHierarchyQuery(module: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'adam_intop', 'python_files', 'hierarchy', module],
    queryFn: () => apiGet<PythonHierarchyPage>(`${BASE}/hierarchy/${module}`),
    enabled: module > 0,
  })
}

/** Raspunsul scrierilor: `stored` lipseste cand nu s-a trimis continut. */
export interface PythonSaveResult {
  ok?: boolean
  ID?: number
  stored?: boolean
  message?: string
}

/** `create_python_file_save`: fisa fisierului si textul din editor. */
export function createPythonFile(form: PythonFileForm, editor: string) {
  return apiPost<PythonSaveResult>(BASE, { ...form, editor })
}

/** `edit_python_file_save`. */
export function savePythonFile(file: number, form: PythonFileForm, editor: string) {
  return apiPost<PythonSaveResult>(`${BASE}/${file}`, { ...form, editor })
}

/**
 * `delete_python_file`, dar cu id-ul in adresa.
 *
 * In portal id-ul venea din corp, si acelasi cap era chemat si de butonul de
 * stergere al MODULELOR extinse - deci un modul sters lasa in urma un fisier
 * sters cu id-ul lui. Aici cele doua stergeri au adrese diferite.
 */
export function removePythonFile(file: number) {
  return apiPost<{ ok?: boolean }>(`${BASE}/${file}/remove`, {})
}

/** `save_python_files_order`: sirul JSON facut de `sortable("serialize")`. */
export function savePythonFilesOrder(module: number, order: Array<{ id: number; children: unknown }>) {
  return apiPost<{ ok?: boolean }>(`${BASE}/hierarchy/${module}`, { data_sortable: JSON.stringify(order) })
}
